/**
 * Loyalty early-access storefront UX (not the security boundary).
 * Checkout Validation Function enforces rules server-side.
 */
(function () {
  const ROOT_SELECTOR = '[data-loyalty-ea-root]';
  const STORE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
  const REFRESH_EVENT = 'loyalty-ea:refresh';

  function unrestricted() {
    return {
      kind: 'unrestricted',
      isCampaignVariant: false,
      blocksAllVariants: false,
      disableSubmit: false,
      maxQuantity: null,
      messageKey: null,
      showSignInLink: false,
      showLoyaltySignupLink: false,
    };
  }

  function blocked(overrides) {
    return {
      kind: 'blocked',
      isCampaignVariant: true,
      blocksAllVariants: false,
      disableSubmit: true,
      maxQuantity: 0,
      messageKey: null,
      showSignInLink: false,
      showLoyaltySignupLink: false,
      ...overrides,
    };
  }

  function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  function isValidStoreDate(value) {
    if (typeof value !== 'string' || !STORE_DATE_PATTERN.test(value)) {
      return false;
    }
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }

  function normalizeVariantId(value) {
    if (value === null || value === undefined) {
      return null;
    }
    const trimmed = String(value).trim();
    const gid = trimmed.match(/^gid:\/\/shopify\/ProductVariant\/(\d+)$/);
    if (gid) {
      return gid[1];
    }
    return /^\d+$/.test(trimmed) ? trimmed : null;
  }

  function clientStoreDate(ianaTimezone) {
    try {
      const timeZone =
        ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());
      const year = parts.find((part) => part.type === 'year')?.value;
      const month = parts.find((part) => part.type === 'month')?.value;
      const day = parts.find((part) => part.type === 'day')?.value;
      if (!year || !month || !day) {
        return null;
      }
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    } catch (error) {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${now.getFullYear()}-${month}-${day}`;
    }
  }

  function resolveStoreDate(boot) {
    if (isValidStoreDate(boot.storeDate)) {
      return boot.storeDate;
    }
    return clientStoreDate(boot.shopTimezone) || boot.storeDate;
  }

  /**
   * Theme UX classifier only. Does not mirror the app contract package.
   */
  function classifyCampaignForUi(raw, storeDate) {
    if (raw === null || raw === undefined) {
      return { kind: 'absent' };
    }
    if (!isPlainObject(raw) || raw.schemaVersion !== 1) {
      return { kind: 'invalid', target: 'product' };
    }
    if (raw.enabled === false) {
      return { kind: 'disabled' };
    }
    if (!isValidStoreDate(storeDate)) {
      return { kind: 'invalid', target: 'product' };
    }
    if (!isValidStoreDate(raw.startDate) || !isValidStoreDate(raw.endDate)) {
      return { kind: 'invalid', target: 'product' };
    }
    if (raw.startDate > raw.endDate) {
      return { kind: 'invalid', target: 'product' };
    }
    if (storeDate > raw.endDate) {
      return { kind: 'ended' };
    }

    const scope = raw.variantScope;
    if (scope !== 'all' && scope !== 'selected') {
      return { kind: 'invalid', target: 'product' };
    }

    let variantIds = [];
    if (Array.isArray(raw.variantIds)) {
      variantIds = raw.variantIds
        .map((id) => normalizeVariantId(id))
        .filter(Boolean);
    }

    if (scope === 'all' && variantIds.length > 0) {
      return { kind: 'invalid', target: 'product' };
    }
    if (scope === 'selected' && variantIds.length === 0) {
      return { kind: 'invalid', target: 'product' };
    }

    const purchaseLimit = raw.purchaseLimit;
    const eligibilityTags = Array.isArray(raw.eligibilityTags)
      ? raw.eligibilityTags
          .filter((tag) => typeof tag === 'string' && tag.trim())
          .map((tag) => tag.trim())
      : [];
    if (
      eligibilityTags.length === 0 ||
      typeof purchaseLimit !== 'number' ||
      !Number.isSafeInteger(purchaseLimit) ||
      purchaseLimit <= 0
    ) {
      return {
        kind: 'invalid',
        target: 'campaign',
        membership: { variantScope: scope, variantIds },
      };
    }

    const config = {
      variantScope: scope,
      variantIds: scope === 'all' ? [] : variantIds,
      eligibilityTags,
      purchaseLimit,
      startDate: raw.startDate,
      endDate: raw.endDate,
    };

    if (storeDate < raw.startDate) {
      return { kind: 'before', config };
    }
    return { kind: 'active', config };
  }

  function isCampaignVariant(variantId, config) {
    if (!config) {
      return false;
    }
    if (config.variantScope === 'all') {
      return true;
    }
    return config.variantIds.includes(variantId);
  }

  function parseLedgerForUi(ledgerPresent, ledgerRaw) {
    if (!ledgerPresent) {
      return { ok: true, variants: {} };
    }
    if (!isPlainObject(ledgerRaw) || ledgerRaw.version !== 1) {
      return { ok: false };
    }
    if ('products' in ledgerRaw) {
      return { ok: false };
    }
    if (!isPlainObject(ledgerRaw.variants)) {
      return { ok: false };
    }
    const variants = {};
    for (const [key, entry] of Object.entries(ledgerRaw.variants)) {
      if (!/^\d+$/.test(key) || !isPlainObject(entry)) {
        return { ok: false };
      }
      const quantity = entry.quantity;
      if (
        typeof quantity !== 'number' ||
        !Number.isSafeInteger(quantity) ||
        quantity < 0
      ) {
        return { ok: false };
      }
      variants[key] = quantity;
    }
    return { ok: true, variants };
  }

  function resolveUiState(input) {
    const variantId = normalizeVariantId(input.variantId);
    if (!variantId) {
      return unrestricted();
    }

    const runtime = classifyCampaignForUi(input.campaignRaw, input.storeDate);

    if (
      runtime.kind === 'absent' ||
      runtime.kind === 'disabled' ||
      runtime.kind === 'ended'
    ) {
      return unrestricted();
    }

    if (runtime.kind === 'invalid') {
      if (runtime.target === 'product') {
        return blocked({
          blocksAllVariants: true,
          messageKey: 'configuration_unavailable',
        });
      }
      const inMembership = isCampaignVariant(variantId, {
        variantScope: runtime.membership.variantScope,
        variantIds: runtime.membership.variantIds,
      });
      if (!inMembership) {
        return unrestricted();
      }
      return blocked({ messageKey: 'configuration_unavailable' });
    }

    const { config } = runtime;
    if (!isCampaignVariant(variantId, config)) {
      return unrestricted();
    }

    if (runtime.kind === 'before') {
      return blocked({ messageKey: 'not_started' });
    }

    if (!input.customerLoggedIn) {
      return blocked({ messageKey: 'sign_in', showSignInLink: true });
    }

    const tags = new Set(input.customerTags || []);
    if (!config.eligibilityTags.some((tag) => tags.has(tag))) {
      return blocked({
        messageKey: 'loyalty_required',
        showLoyaltySignupLink: true,
      });
    }

    const ledger = parseLedgerForUi(input.ledgerPresent, input.ledgerRaw);
    if (!ledger.ok) {
      return blocked({ messageKey: 'ledger_unavailable' });
    }

    const cartQtyTotal = input.cartQtyByVariant?.[variantId] || 0;
    const historical = ledger.variants[variantId] || 0;
    const lineQty =
      typeof input.lineQuantity === 'number' ? input.lineQuantity : 0;
    const cartQtyForAllowance =
      typeof input.lineQuantity === 'number'
        ? Math.max(0, cartQtyTotal - lineQty)
        : cartQtyTotal;
    const remaining = Math.max(
      0,
      config.purchaseLimit - historical - cartQtyForAllowance,
    );

    if (remaining <= 0) {
      return blocked({ messageKey: 'limit_reached' });
    }

    return {
      kind: 'limited',
      isCampaignVariant: true,
      blocksAllVariants: false,
      disableSubmit: false,
      maxQuantity: remaining,
      messageKey: null,
      showSignInLink: false,
      showLoyaltySignupLink: false,
    };
  }

  function getMessages() {
    return globalThis.loyaltyEarlyAccessStrings || {};
  }

  function getLoginUrl() {
    // Relative path only (Shopify return_to). Uses the current page so
    // collection quick-shop returns to the collection, PDP to the product.
    const returnTo = `${window.location.pathname}${window.location.search}`;
    const base =
      globalThis.routes?.storefront_login_url ||
      '/customer_authentication/login';

    try {
      const url = new URL(base, window.location.origin);
      url.searchParams.set('return_to', returnTo);
      return `${url.pathname}${url.search}`;
    } catch (error) {
      const separator = base.includes('?') ? '&' : '?';
      return `${base}${separator}return_to=${encodeURIComponent(returnTo)}`;
    }
  }

  function getRewardsUrl() {
    return globalThis.routes?.loyalty_rewards_url || '/pages/rewards';
  }

  function getNoticeLink(ui, messages) {
    if (ui.messageKey === 'sign_in' && ui.showSignInLink) {
      return {
        href: getLoginUrl(),
        label: messages.sign_in_link_label || 'Sign in',
      };
    }
    if (ui.messageKey === 'loyalty_required' && ui.showLoyaltySignupLink) {
      return {
        href: getRewardsUrl(),
        label: messages.loyalty_required_link_label || 'Sign up',
      };
    }
    return null;
  }

  function parseBoot(root) {
    const node = root.querySelector('[data-loyalty-ea-boot]');
    if (!node) {
      return null;
    }
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('loyalty-early-access: invalid boot JSON', error);
      return null;
    }
  }

  function getVariantId(root) {
    if (root.dataset.loyaltyEaMode === 'cart') {
      return root.dataset.variantId || '';
    }
    const form =
      root.closest('product-form')?.querySelector('form[data-type="add-to-cart-form"]') ||
      root.querySelector('form[data-type="add-to-cart-form"]');
    const idInput = form?.querySelector('input[name="id"]');
    return idInput?.value || root.dataset.variantId || '';
  }

  function getLineQuantity(root) {
    if (root.dataset.loyaltyEaMode !== 'cart') {
      return undefined;
    }
    const qtyInput =
      root.querySelector('[data-loyalty-ea-qty]') ||
      root.querySelector('[data-quantity-input-field]');
    if (!qtyInput) {
      return 0;
    }
    const parsed = parseInt(qtyInput.value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function findSubmitButton(root) {
    return (
      root.querySelector('[data-loyalty-ea-submit]') ||
      root.querySelector('.product-form__submit[name="add"]') ||
      root.querySelector('button[name="add"]')
    );
  }

  function findQuantityInput(root) {
    return (
      root.querySelector('[data-loyalty-ea-qty]') ||
      root.querySelector('quantity-input [data-quantity-input-field]') ||
      root.querySelector('.quantity__input[name="quantity"]')
    );
  }

  function rememberSubmitLabel(button) {
    if (!button || button.dataset.loyaltyEaLabelSaved === 'true') {
      return;
    }
    button.dataset.loyaltyEaOriginalLabel = button.innerHTML;
    button.dataset.loyaltyEaLabelSaved = 'true';
  }

  function restoreSubmitLabel(button) {
    if (!button || !button.dataset.loyaltyEaOriginalLabel) {
      return;
    }
    button.innerHTML = button.dataset.loyaltyEaOriginalLabel;
  }

  /**
   * Cart lines use a short limit copy; PDP keeps the longer limit_reached string.
   * @param {ReturnType<typeof resolveUiState>} ui
   * @param {boolean} isCart
   * @param {number|undefined} lineQuantity
   */
  function resolveNoticeUi(ui, isCart, lineQuantity) {
    if (!isCart) {
      return ui;
    }

    if (ui.kind === 'blocked' && ui.messageKey === 'limit_reached') {
      return { ...ui, messageKey: 'limit_reached_cart' };
    }

    if (
      ui.kind === 'limited' &&
      typeof ui.maxQuantity === 'number' &&
      typeof lineQuantity === 'number' &&
      lineQuantity >= ui.maxQuantity
    ) {
      return { ...ui, messageKey: 'limit_reached_cart' };
    }

    return ui;
  }

  function ensureNoticeId(notice, root) {
    if (!notice.id) {
      const variantPart = root.dataset.variantId || root.dataset.productId || '';
      notice.id = `loyalty-ea-notice-${variantPart || Math.random().toString(36).slice(2, 9)}`;
    }
    return notice.id;
  }

  function renderNotice(notice, ui) {
    if (!notice) {
      return;
    }

    const showNotice =
      Boolean(ui.messageKey) && (ui.kind === 'blocked' || ui.kind === 'limited');

    if (!showNotice) {
      notice.hidden = true;
      notice.textContent = '';
      return;
    }

    const messages = getMessages();
    const text = messages[ui.messageKey] || '';
    const noticeLink = getNoticeLink(ui, messages);

    if (noticeLink) {
      const link = document.createElement('a');
      link.href = noticeLink.href;
      link.textContent = noticeLink.label;
      notice.textContent = '';
      const parts = String(text).split('[[link]]');
      if (parts.length > 1) {
        notice.append(document.createTextNode(parts[0]));
        notice.append(link);
        notice.append(document.createTextNode(parts[1] || ''));
      } else {
        notice.append(document.createTextNode(`${text} `));
        notice.append(link);
      }
    } else {
      notice.textContent = text;
    }

    notice.hidden = false;
  }

  function parsePositiveMax(value) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function inventoryMaxFromInput(qtyInput) {
    const current = qtyInput.getAttribute('max');
    const lastApplied = qtyInput.dataset.loyaltyEaAppliedMax;
    if (lastApplied !== undefined && current === lastApplied) {
      return parsePositiveMax(qtyInput.dataset.loyaltyEaInventoryMax);
    }
    qtyInput.dataset.loyaltyEaInventoryMax = current == null ? '' : current;
    return parsePositiveMax(current);
  }

  function applyCampaignQuantityMax(qtyInput, eaMax, minFloor) {
    const inventoryMax = inventoryMaxFromInput(qtyInput);
    let next = eaMax;
    if (inventoryMax != null) {
      next = Math.min(inventoryMax, eaMax);
    }
    const value = String(Math.max(minFloor, next));
    qtyInput.setAttribute('max', value);
    qtyInput.dataset.loyaltyEaAppliedMax = value;
  }

  function clearCampaignQuantityCap(qtyInput) {
    const current = qtyInput.getAttribute('max');
    const lastApplied = qtyInput.dataset.loyaltyEaAppliedMax;
    if (lastApplied !== undefined && current === lastApplied) {
      const stored = qtyInput.dataset.loyaltyEaInventoryMax;
      if (stored === '') {
        qtyInput.removeAttribute('max');
      } else if (stored !== undefined) {
        qtyInput.setAttribute('max', stored);
      }
    } else if (current != null) {
      qtyInput.dataset.loyaltyEaInventoryMax = current;
    }
    delete qtyInput.dataset.loyaltyEaAppliedMax;
  }

  function applyUiState(root, ui) {
    const submit = findSubmitButton(root);
    const qtyInput = findQuantityInput(root);
    const notice = root.querySelector('[data-loyalty-ea-notice]');
    const shouldGate = ui.blocksAllVariants || ui.isCampaignVariant;
    const isCart = root.dataset.loyaltyEaMode === 'cart';

    if (!shouldGate) {
      if (submit) {
        rememberSubmitLabel(submit);
        restoreSubmitLabel(submit);
        submit.removeAttribute('aria-describedby');
        if (submit.dataset.shopifyDisabled !== 'true') {
          submit.disabled = false;
        }
      }
      if (qtyInput) {
        qtyInput.removeAttribute('aria-describedby');
        clearCampaignQuantityCap(qtyInput);
        qtyInput.closest('quantity-input')?.updateButtonStates?.();
      }
      if (notice) {
        notice.hidden = true;
        notice.textContent = '';
      }
      return;
    }

    const noticeUi = resolveNoticeUi(ui, isCart, getLineQuantity(root));
    renderNotice(notice, noticeUi);

    if (submit) {
      rememberSubmitLabel(submit);
      if (ui.disableSubmit) {
        submit.disabled = true;
      } else if (submit.dataset.shopifyDisabled !== 'true') {
        submit.disabled = false;
        restoreSubmitLabel(submit);
      }

      if (notice && !notice.hidden) {
        submit.setAttribute('aria-describedby', ensureNoticeId(notice, root));
      } else {
        submit.removeAttribute('aria-describedby');
      }
    }

    if (qtyInput && ui.maxQuantity !== null && ui.maxQuantity !== undefined) {
      const quantityInput = qtyInput.closest('quantity-input');
      const plusButton = quantityInput?.querySelector('button[name="plus"]');

      if (isCart) {
        // Do not set max=0 (or max < current) on cart lines — that breaks − / remove.
        // For blocked lines, clear max and disable + only so guests can still remove.
        if (ui.kind === 'blocked') {
          qtyInput.removeAttribute('max');
          if (plusButton) {
            plusButton.disabled = true;
          }
        } else if (ui.kind === 'limited') {
          applyCampaignQuantityMax(
            qtyInput,
            ui.maxQuantity,
            parseInt(qtyInput.getAttribute('min') || '0', 10),
          );
          quantityInput?.updateButtonStates?.();
        }
      } else if (ui.kind === 'blocked') {
        inventoryMaxFromInput(qtyInput);
        qtyInput.setAttribute('max', '0');
        qtyInput.dataset.loyaltyEaAppliedMax = '0';
        quantityInput?.updateButtonStates?.();
      } else if (ui.kind === 'limited') {
        applyCampaignQuantityMax(
          qtyInput,
          ui.maxQuantity,
          parseInt(qtyInput.getAttribute('min') || '1', 10),
        );
        quantityInput?.updateButtonStates?.();
      }
    }

    if (isCart && qtyInput) {
      if (notice && !notice.hidden) {
        qtyInput.setAttribute('aria-describedby', ensureNoticeId(notice, root));
      } else {
        qtyInput.removeAttribute('aria-describedby');
      }
    }
  }

  function refreshRoot(root, variantIdOverride) {
    const boot = parseBoot(root);
    if (!boot) {
      return;
    }

    const ui = resolveUiState({
      variantId: variantIdOverride || getVariantId(root),
      storeDate: resolveStoreDate(boot),
      campaignRaw: boot.campaignRaw,
      customerLoggedIn: boot.customerLoggedIn,
      customerTags: boot.customerTags,
      ledgerPresent: boot.ledgerPresent,
      ledgerRaw: boot.ledgerRaw,
      cartQtyByVariant: boot.cartQtyByVariant || {},
      lineQuantity: getLineQuantity(root),
    });

    root.dataset.loyaltyEaState = ui.kind;
    applyUiState(root, ui);
    return ui;
  }

  async function refreshCartQtyMaps() {
    let cart;
    try {
      const response = await fetch(`${globalThis.routes?.cart_url || '/cart'}.js`);
      cart = await response.json();
    } catch (error) {
      console.error('loyalty-early-access: cart.js fetch failed', error);
      return;
    }

    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const bootNode = root.querySelector('[data-loyalty-ea-boot]');
      if (!bootNode) {
        return;
      }
      const boot = parseBoot(root);
      if (!boot) {
        return;
      }

      const totals = {};
      Object.keys(boot.cartQtyByVariant || {}).forEach((id) => {
        totals[id] = 0;
      });
      cart.items.forEach((item) => {
        const vid = String(item.variant_id);
        if (Object.prototype.hasOwnProperty.call(totals, vid)) {
          totals[vid] += item.quantity;
        }
      });

      boot.cartQtyByVariant = totals;
      bootNode.textContent = JSON.stringify(boot);
      refreshRoot(root);
    });
  }

  function refreshAll(variantId) {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      refreshRoot(root, variantId);
    });
  }

  function refreshInScope(scopeEl, variantId) {
    const section = scopeEl?.closest?.('.shopify-section');
    const roots = section
      ? section.querySelectorAll(ROOT_SELECTOR)
      : document.querySelectorAll(ROOT_SELECTOR);
    roots.forEach((root) => {
      refreshRoot(root, variantId);
    });
  }

  function bindSubmitGuard(root) {
    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    if (!form || form.dataset.loyaltyEaSubmitBound === 'true') {
      return;
    }
    form.dataset.loyaltyEaSubmitBound = 'true';
    form.addEventListener('submit', (event) => {
      const ui = refreshRoot(root);
      if (ui && ui.disableSubmit) {
        event.preventDefault();
      }
    });
  }

  function captureSoldOutSubmit(root) {
    if (root.dataset.loyaltyEaCapturedSoldOut === 'true') {
      return;
    }
    root.dataset.loyaltyEaCapturedSoldOut = 'true';
    const submit = findSubmitButton(root);
    if (submit && submit.disabled) {
      submit.dataset.shopifyDisabled = 'true';
    }
  }

  function initRoot(root) {
    captureSoldOutSubmit(root);
    bindSubmitGuard(root);
    refreshRoot(root);
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  function onCartUpdated() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      captureSoldOutSubmit(root);
      bindSubmitGuard(root);
    });
    refreshCartQtyMaps();
  }

  function onRefreshEvent(event) {
    const { variantId, scope } = event.detail || {};
    if (scope) {
      refreshInScope(scope, variantId);
      return;
    }
    refreshAll(variantId);
  }

  /**
   * Cart qty gate. Decreases and removals (incl. qty 0) always allowed.
   * Only quantity increases are checked against early-access allowance.
   * @param {HTMLInputElement} input
   * @param {number} proposedQuantity
   * @param {number} previousQuantity line qty before this change
   */
  function validateCartQuantityIncrease(input, proposedQuantity, previousQuantity) {
    const root = input.closest(ROOT_SELECTOR);
    if (!root || root.dataset.loyaltyEaMode !== 'cart') {
      return { ok: true };
    }

    const prev = Number(previousQuantity);
    const next = Number(proposedQuantity);
    const prevQty = Number.isFinite(prev) ? prev : 0;
    const nextQty = Number.isFinite(next) ? next : 0;

    // Decreases and removals (including qty 0) are always allowed.
    if (nextQty <= prevQty) {
      return { ok: true };
    }

    const boot = parseBoot(root);
    if (!boot) {
      return { ok: true };
    }

    // Use previous line qty so other cart units of this variant still count,
    // without treating the in-progress decrease as already applied.
    const ui = resolveUiState({
      variantId: root.dataset.variantId || '',
      storeDate: resolveStoreDate(boot),
      campaignRaw: boot.campaignRaw,
      customerLoggedIn: boot.customerLoggedIn,
      customerTags: boot.customerTags,
      ledgerPresent: boot.ledgerPresent,
      ledgerRaw: boot.ledgerRaw,
      cartQtyByVariant: boot.cartQtyByVariant || {},
      lineQuantity: prevQty,
    });

    if (!ui.isCampaignVariant && !ui.blocksAllVariants) {
      return { ok: true };
    }

    const messages = getMessages();

    if (ui.kind === 'blocked') {
      const messageKey =
        ui.messageKey === 'limit_reached'
          ? 'limit_reached_cart'
          : ui.messageKey;
      return {
        ok: false,
        message:
          messages[messageKey] ||
          messages.configuration_unavailable ||
          '',
      };
    }

    if (ui.kind === 'limited' && nextQty > ui.maxQuantity) {
      return {
        ok: false,
        message: messages.limit_reached_cart || messages.quantity_exceeds || '',
      };
    }

    return { ok: true };
  }

  globalThis.LoyaltyEarlyAccess = {
    init,
    refresh: refreshRoot,
    refreshAll,
    refreshCartQtyMaps,
    validateCartQuantityIncrease,
    REFRESH_EVENT,
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener(REFRESH_EVENT, onRefreshEvent);
  document.addEventListener('ajaxProduct:added', refreshCartQtyMaps);
  document.addEventListener('cart:updated', onCartUpdated);
})();
